'use client';

import { useEsiteCtx } from 'services/esite/context';
import { CatButton, CatIconShop } from '../../blocks-components';
import { useTranslations } from 'next-intl';
import { useQuickToast } from 'hooks/use-toast';
import { RelatedPartsPriceObject } from 'services/product/types';
import { ProductPriceAvailability } from 'services/priceAvailability/types';
import { ProductsPriceData } from 'app/[lang]/[esite]/(core)/search/_components/analytics-listener/types';
import {
  getFormattedItemData,
  getFormattedItemSummary,
  getFormattedSearchSelectItem
} from 'components/product-comparison-grid/product-comparison-card/utils';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { OrderItemPart } from 'services/cart/types';
import { useEffect, useState } from 'react';
import { useAddToCartItemEvent } from 'hooks/analytics/use-add-to-cart-item';
import styles from './add-to-cart.module.scss';
import { useCoreContext } from 'providers/core-provider';

export interface AddtoCartProps {
  productPrice: RelatedPartsPriceObject | ProductPriceAvailability;
  typeIcon?: boolean;
  fullWidth?: boolean;
  productPriceData?: ProductsPriceData;
  cartId?: string;
  locationId?: string;
  orderItem?: OrderItemPart;
  size?: 'lg' | 'xs' | 'sm' | 'md';
  buttonLabel?: boolean;
}

export default function AddToCart({
  productPrice,
  typeIcon,
  fullWidth = true,
  productPriceData,
  locationId,
  orderItem,
  cartId,
  size = 'sm',
  buttonLabel = false
}: Readonly<AddtoCartProps>) {
  const t = useTranslations();
  const { storeId, locale } = useEsiteCtx();
  const toast = useQuickToast();
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const { fireEvent: addToCartItemEvent } = useAddToCartItemEvent();
  const [addToCartButtonClicked, setAddToCartButtonClicked] = useState(false);
  const { runAddToCart, isAddingToCart } = useCoreContext();

  const {
    partNumber,
    partName = productPrice.name,
    quantity,
    sosCode
  } = productPrice;

  const onClickHandler = async () => {
    const items = Array.isArray(productPrice)
      ? productPrice.map(({ partNumber, partName, quantity, sosCode }) => ({
          partNumber,
          partName,
          quantity,
          sosCode
        }))
      : [
          {
            partNumber,
            partName,
            quantity,
            sosCode
          }
        ];
    const { error } = await runAddToCart(
      {
        items
      },
      storeId,
      locale
    );
    if (error) {
      toast.error({
        message: t('ITEM_ADD_ERROR'),
        className: styles['cat-c-toast']
      });
    } else {
      toast.success({
        message: t('ITEM_ADDED_TO_CART'),
        className: styles['cat-c-toast']
      });
    }
    setAddToCartButtonClicked(true);
  };

  useEffect(() => {
    if (!isAddingToCart && addToCartButtonClicked) {
      fireSelectItemEvent({
        item: getFormattedSearchSelectItem(orderItem, productPriceData),
        partName:
          productPriceData &&
          `${productPriceData?.partNumber}: ${productPriceData?.name}`,
        locationId,
        itemSummary: {
          categoryPath: productPriceData?.productPriceData?.categoryPath,
          manufacturer: productPriceData?.manufacturer
        },
        isSearchPageEvent: true,
        itemListId: productPriceData?.listNameAndId,
        itemListName: productPriceData?.listNameAndId
      });

      addToCartItemEvent({
        locationId,
        itemSummary:
          productPriceData && getFormattedItemSummary(productPriceData),
        itemData: getFormattedItemData(cartId, productPriceData),
        itemListId: productPriceData?.listNameAndId,
        itemListName: productPriceData?.listNameAndId
      });

      setAddToCartButtonClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddingToCart]);

  return (
    <>
      {typeIcon ? (
        <CatButton
          variant="primary"
          size={size}
          hideText
          data-testid="cat-button"
          onClick={onClickHandler}
        >
          <CatIconShop slot="before"></CatIconShop>
        </CatButton>
      ) : (
        <CatButton
          variant="primary"
          size={size}
          fullWidth={fullWidth}
          onClick={onClickHandler}
        >
          {buttonLabel ? t('PM_ADD_ALL_TO_CART') : t('ADD_TO_CART')}
        </CatButton>
      )}
    </>
  );
}
